import { getUpstreamTasks } from '../FlowchartView/utils/edgePaths/edgeGeneration';
import { getCategoryConfig, getStatusCategory } from '../FlowchartView/utils/statusUtils';
import { NEST_Z_STEP, SAME_LEVEL_Z } from './constants';
import { computeLayout, LAYOUT_REVISION, quantizeLayoutAspect, UNTAGGED_KEY } from './neuralLayout';
import { contentFrameBounds, contentItemsFromGraph } from './utils/neuralSceneUtils';

/**
 * Neural graph — a 2D compound layout rendered in a 3D scene.
 *
 * Every node is a real task, every wire a real dependency. Nested campaigns are
 * scanned and their tasks become satellite nodes on a layer behind the hub task
 * that owns them, so the whole tree is visible at once.
 *
 * Missions cluster into islands by primary tag (nested missions inherit their
 * root hub's tag). Layout is deterministic (seeded by graph structure) and cached
 * by structure, so status edits never move nodes and the same project always
 * draws the same picture.
 */

const MAX_NEST_DEPTH = 2;
const LAYOUT_CACHE_LIMIT = 8;
const layoutCache = new Map();

/** Fallback island tints when a tag has no colour of its own. */
export const CLUSTER_TINTS = [
  '#5b7cff', '#3fd6c0', '#9b6dff', '#ff6fae',
  '#4fb8ff', '#8fe36a', '#ffa25c', '#ff6b8a',
  '#7fd8ff', '#c48cff',
];
/** The untagged island has no cloud; this tint only reaches pods and wires. */
const UNTAGGED_TINT = '#6b74a8';

function resolveNodeStatusPresentation(task, statuses) {
  const category = getStatusCategory(task.status, statuses);
  const list = Array.isArray(statuses) ? statuses : [];
  const match = list.find((s) => s.id === task.status);
  const colorHex = match?.color ?? getCategoryConfig(category).color;
  return { category, colorHex };
}

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Primary tag name (first tag), with legacy category fallback. */
export function clusterKeyForTask(task) {
  const first = Array.isArray(task.tags) && task.tags.length > 0 ? task.tags[0] : null;
  const name = typeof first === 'string' ? first : first?.name;
  if (name) return name;
  if (typeof task.category === 'string' && task.category) return task.category;
  return UNTAGGED_KEY;
}

/**
 * Flatten the root sprint plus nested campaigns into "units".
 * unit = { task, statuses, hubIndex, depth, id }
 */
function collectUnits(tasks, statuses, nestedSprints, allSprints) {
  const units = [];
  /** @type {Array<[number, number]>} dependency edges: [upstream, dependent] */
  const depEdges = [];
  /** @type {Array<[number, number]>} drawn nesting wires: [hub task, child entry task] */
  const nestEdges = [];
  /** @type {Array<[number, number]>} drawn return wires: [child leaf task, hub task] */
  const returnEdges = [];
  /** @type {Array<[number, number]>} layout-only tethers: [hub task, every child task] */
  const nestSprings = [];
  const findSprint = (id) => (
    nestedSprints.find((s) => s?.id === id) || allSprints.find((s) => s?.id === id) || null
  );

  function addScope(scopeTasks, scopeStatuses, hubIndex, depth, idPrefix, visitedSprints) {
    const withIds = scopeTasks.map((t, idx) => (t.id ? t : { ...t, id: `__synth_${idx}` }));
    const start = units.length;
    const localIndex = new Map();
    withIds.forEach((task, i) => {
      localIndex.set(task.id, start + i);
      units.push({
        task,
        statuses: scopeStatuses,
        hubIndex,
        depth,
        id: idPrefix ? `${idPrefix}::${task.id}` : task.id,
      });
    });

    withIds.forEach((task, i) => {
      const gi = start + i;
      const seen = new Set();
      const add = (upId) => {
        const gj = localIndex.get(upId);
        if (gj !== undefined && gj !== gi && !seen.has(gj)) {
          seen.add(gj);
          depEdges.push([gj, gi]);
        }
      };
      // Unlock conditions and dependsOn both — nested campaigns often only have one.
      for (const { taskId: upId } of getUpstreamTasks(task, withIds)) {
        if (upId) add(upId);
      }
      if (Array.isArray(task.dependsOn)) {
        for (const dep of task.dependsOn) {
          if (typeof dep === 'string') add(dep);
          else if (dep && typeof dep === 'object') {
            if (dep.taskId) add(dep.taskId);
            if (typeof dep.task === 'number' && dep.task >= 0 && dep.task < withIds.length) {
              add(withIds[dep.task].id);
            }
          } else if (typeof dep === 'number' && dep >= 0 && dep < withIds.length) {
            add(withIds[dep].id);
          }
        }
      }
    });

    if (hubIndex !== null) {
      // The nest is a pod with the hub as the gate both ways (parentMissionLock /
      // useProgressSync): hub locked ⇒ everything inside locked; nest 100% ⇒ hub
      // completes ⇒ hub's downstream unlocks. So: hub → entry tasks (nothing inside
      // depends on them), inner chain, leaves (nothing inside depends on them
      // downstream) → back to the hub. Every child is tethered for layout only.
      const scopeDeps = depEdges.filter(([, gi]) => gi >= start);
      const hasUpstreamInScope = new Set(scopeDeps.map(([, gi]) => gi));
      const hasDownstreamInScope = new Set(scopeDeps.map(([gj]) => gj));
      // A hub with auto-sync switched off really is a dead end: no return wires.
      const closesLoop = units[hubIndex].task.statusAutoSync !== false;
      withIds.forEach((_, i) => {
        const gi = start + i;
        nestSprings.push([hubIndex, gi]);
        if (!hasUpstreamInScope.has(gi)) nestEdges.push([hubIndex, gi]);
        if (closesLoop && !hasDownstreamInScope.has(gi)) returnEdges.push([gi, hubIndex]);
      });
    }

    if (depth >= MAX_NEST_DEPTH) return;
    withIds.forEach((task, i) => {
      if (!task.nestedSprintId || visitedSprints.has(task.nestedSprintId)) return;
      const child = findSprint(task.nestedSprintId);
      if (!child || !Array.isArray(child.tasks) || child.tasks.length === 0) return;
      const nextVisited = new Set(visitedSprints);
      nextVisited.add(child.id);
      addScope(child.tasks, child.statuses || scopeStatuses, start + i, depth + 1, units[start + i].id, nextVisited);
    });
  }

  addScope(tasks, statuses, null, 0, '', new Set());
  return { units, depEdges, nestEdges, returnEdges, nestSprings };
}

/** Layout is a function of ids, edges, tethers and island membership — not status or colour. */
function structureKey(units, depEdges, nestSprings, aspect) {
  return [
    LAYOUT_REVISION,
    aspect,
    units.map((u) => `${u.id}@${u.hubIndex === null ? clusterKeyForTask(u.task) : ''}`).join('\0'),
    depEdges.map((e) => e.join('>')).join('|'),
    nestSprings.map((e) => e.join('~')).join('|'),
  ].join('#');
}

function cachedLayout(units, depEdges, nestSprings, aspect) {
  const key = structureKey(units, depEdges, nestSprings, aspect);
  const hit = layoutCache.get(key);
  if (hit) return hit;
  const layout = computeLayout({
    units,
    depEdges,
    nestSprings,
    clusterKeyOf: clusterKeyForTask,
    seed: hashString(key),
    aspect,
  });
  layoutCache.set(key, layout);
  if (layoutCache.size > LAYOUT_CACHE_LIMIT) {
    const oldest = layoutCache.keys().next().value;
    layoutCache.delete(oldest);
  }
  return layout;
}

/** Island long-axis + how stretched it is, from the real root positions. */
function islandAxis(points, cx, cy, fallbackAngle) {
  let xx = 0;
  let xy = 0;
  let yy = 0;
  points.forEach(([px, py]) => {
    const ox = px - cx;
    const oy = py - cy;
    xx += ox * ox;
    xy += ox * oy;
    yy += oy * oy;
  });
  const disc = Math.sqrt(Math.max(0, (xx - yy) * (xx - yy) + 4 * xy * xy));
  const ev = (xx + yy + disc) * 0.5;
  let ax = xy;
  let ay = ev - xx;
  if (Math.hypot(ax, ay) < 1e-5) {
    ax = Math.cos(fallbackAngle);
    ay = Math.sin(fallbackAngle);
  }
  const len = Math.hypot(ax, ay) || 1;
  const ratio = (Math.max(xx, yy) + 0.02) / (Math.min(xx, yy) + 0.02);
  return {
    axis: [ax / len, ay / len],
    stretch: 1.08 + Math.min(1.05, (ratio - 1) * 0.32),
  };
}

/** Cheap per-island weather: unique seed and a modest stretch. No extra field points. */
function characteriseClusters(clusters) {
  let gx = 0;
  let gy = 0;
  let n = 0;
  clusters.forEach((c) => {
    if (c.key === UNTAGGED_KEY) return;
    gx += c.cx;
    gy += c.cy;
    n += 1;
  });
  if (n) { gx /= n; gy /= n; }

  clusters.forEach((c) => {
    if (c.key === UNTAGGED_KEY) return;
    const angle = Math.atan2(c.cy - gy, c.cx - gx);
    const { axis, stretch } = islandAxis(c.points, c.cx, c.cy, angle);
    const h = hashString(c.key);
    Object.assign(c, {
      axis,
      stretch: Math.min(1.45, stretch),
      seed: [((h & 255) / 255 - 0.5) * 0.7, (((h >> 8) & 255) / 255 - 0.5) * 0.7],
      timeMul: 0.72 + ((h >> 18) & 7) * 0.05,
      timeOff: ((h >> 4) & 255) * 0.09 + angle,
    });
  });
}

/** Silhouette + a few interior roots — the smoke field follows these points. */
function pickCloudPoints(idxs, x, y, cx, cy, maxPts) {
  if (!idxs.length) return [];
  if (idxs.length <= maxPts) return idxs.map((i) => [x[i], y[i]]);
  const scored = idxs.map((i) => [i, Math.hypot(x[i] - cx, y[i] - cy)]);
  scored.sort((a, b) => b[1] - a[1]);
  const picked = new Set();
  const rim = Math.min(12, maxPts);
  for (let k = 0; k < rim; k += 1) picked.add(scored[k][0]);
  const step = idxs.length / Math.max(1, maxPts - picked.size);
  for (let k = 0; picked.size < maxPts; k += 1) {
    picked.add(idxs[Math.floor(k * step) % idxs.length]);
  }
  return [...picked].map((i) => [x[i], y[i]]);
}

/** Real tag colour when the project defines one; palette otherwise. */
function tintForCluster(key, index, tagColorByName) {
  if (key === UNTAGGED_KEY) return UNTAGGED_TINT;
  const own = tagColorByName?.[key];
  if (typeof own === 'string' && /^#[0-9a-fA-F]{6}$/.test(own.trim())) return own.trim();
  return CLUSTER_TINTS[index % CLUSTER_TINTS.length];
}

/**
 * @param {Array} tasks
 * @param {Array} statuses
 * @param {{ nestedSprints?: Array, allSprints?: Array, tagColorByName?: Record<string,string>, aspect?: number }} [options]
 */
export function buildGraph(tasks, statuses, options = {}) {
  const aspect = quantizeLayoutAspect(options.aspect);
  const layoutKey = `${LAYOUT_REVISION}:${aspect}`;
  if (!tasks || tasks.length === 0) {
    return { nodes: [], edges: [], clusters: [], hulls: [], radius: 12, bounds: { minX: -6, maxX: 6, minY: -4, maxY: 4 }, layoutKey };
  }
  const nestedSprints = Array.isArray(options.nestedSprints) ? options.nestedSprints : [];
  const allSprints = Array.isArray(options.allSprints) ? options.allSprints : [];
  const tagColorByName = options.tagColorByName || null;

  const { units, depEdges, nestEdges, returnEdges, nestSprings } = collectUnits(tasks, statuses, nestedSprints, allSprints);
  const layout = cachedLayout(units, depEdges, nestSprings, aspect);
  const { x, y, size, flow, clusterIndex, clusterKeys, clusterMembers, clusterBoxes, podBoxes, childCount, degree } = layout;
  const N = units.length;

  const rootOf = (k) => (units[k].hubIndex === null ? k : rootOf(units[k].hubIndex));
  const tints = clusterKeys.map((key, c) => tintForCluster(key, c, tagColorByName));

  // Nests drop a whole layer. On the same layer, flow (root→leaf) is a
  // whisper of z so the sheet isn't perfectly flat — sources sit slightly
  // toward the camera, sinks slightly away.
  const z = new Float64Array(N);
  const order = Array.from({ length: N }, (_, i) => i).sort((a, b) => units[a].depth - units[b].depth);
  order.forEach((i) => {
    const relief = (flow[i] - 0.5) * SAME_LEVEL_Z;
    if (units[i].hubIndex === null) {
      z[i] = relief;
    } else {
      z[i] = z[units[i].hubIndex] - NEST_Z_STEP + relief * 0.45;
    }
  });

  const nodes = units.map((u, i) => {
    const { category, colorHex } = resolveNodeStatusPresentation(u.task, u.statuses);
    return {
      id: u.id,
      pos: [x[i], y[i], z[i]],
      statusCategory: category,
      statusColorHex: colorHex,
      isDecision: u.task.nodeType === 'decision',
      name: u.task.name || '',
      size: size[i],
      degree: degree[i],
      flow: flow[i],
      clusterKey: clusterKeys[clusterIndex[i]] ?? UNTAGGED_KEY,
      clusterIndex: clusterIndex[i],
      clusterTint: tints[clusterIndex[i]],
      parentTaskId: u.hubIndex !== null ? units[u.hubIndex].id : null,
      rootTaskId: units[rootOf(i)].id,
      nestDepth: u.depth,
      hasNest: childCount[i] > 0,
      nestCount: childCount[i],
    };
  });

  const edges = [
    ...depEdges.map(([a, b]) => [a, b]),
    ...nestEdges.map(([a, b]) => [a, b, 'nest']),
    ...returnEdges.map(([a, b]) => [a, b, 'return']),
  ];

  // One pocket per pod: the lit "floor" of the nested layer, just behind its nodes.
  const hulls = [];
  podBoxes.forEach((box, h) => {
    hulls.push({
      hubId: units[h].id,
      cx: box.cx,
      cy: box.cy,
      z: z[h] - NEST_Z_STEP - 0.5,
      r: box.halfW + 0.5,
      tint: tints[clusterIndex[h]],
    });
  });

  // Islands: the cloud fits the compound box the engine actually produced.
  const clusters = clusterKeys.map((key, c) => {
    const box = clusterBoxes[c];
    const members = clusterMembers[c];
    const rootsIn = members.filter((i) => units[i].hubIndex === null);
    const nestedIn = members.filter((i) => units[i].hubIndex !== null);
    const cz = rootsIn.length
      ? rootsIn.reduce((s, i) => s + z[i], 0) / rootsIn.length
      : 0;
    const nestZ = nestedIn.length
      ? nestedIn.reduce((s, i) => s + z[i], 0) / nestedIn.length
      : cz;
    return {
      key,
      index: c,
      tint: tints[c],
      cx: box.cx,
      cy: box.cy,
      z: cz,
      nestZ,
      hasNests: nestedIn.length > 0,
      r: Math.max(1.5, Math.hypot(box.halfW, box.halfH) * 0.8),
      halfW: box.halfW,
      halfH: box.halfH,
      count: members.length,
      points: pickCloudPoints(rootsIn, x, y, box.cx, box.cy, 16),
    };
  });
  characteriseClusters(clusters);

  const bounds = contentFrameBounds(contentItemsFromGraph(nodes));
  const radius = Math.max(
    1,
    Math.hypot((bounds.maxX - bounds.minX) * 0.5, (bounds.maxY - bounds.minY) * 0.5),
  );
  return { nodes, edges, clusters, hulls, radius, bounds, layoutKey };
}
